import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { pitchesApi } from "../../repositories/pitches";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon, PencilIcon } from "@heroicons/react/24/solid";
import { ArrowLeftIcon } from "lucide-react";
import Loader from "../../components/common/Loader";

interface PitchData {
  id: string | number;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  focusTrackName?: string;
  trackISRC: string;
  songDescription: string;
  mainGenre: string;
  songMood: string;
  language: string;
  featuredArtists: string;
  shortDescription: string;
  longDescription: string;
  lyrics: string;
  linkToListen: string;
  promoPhotos: string;
  status: string;
  AmazonInfo?: Record<string, any>;
  SpotifyInfo?: Record<string, any>;
  DeezerInfo?: Record<string, any>;
  TidalInfo?: Record<string, any>;
  AudiomackInfo?: Record<string, any>;
  YouTubeInfo?: Record<string, any>;
  SoundCloudInfo?: Record<string, any>;
  AppleInfo?: Record<string, any>;
}

const ViewSubmission: React.FC = () => {
  const [data, setData] = useState<PitchData | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPitchData = async () => {
      try {
        const { data } = await pitchesApi.getOne(Number(id));
        if (!data.success) throw new Error("Failed to fetch data");
        setData(data.data);
      } catch (error) {
        console.error(error);
      }
    };

    if (id) fetchPitchData();
  }, [id]);

  if (!data) return <Loader />;

  const excludedFields = ["id", "pitchId", "updatedAt"];
  return (
    <div className="p-4 mx-auto bg-white rounded-md max-w-7xl">
      {/* Back Button */}
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center mb-4 text-gray-600 hover:text-gray-800"
      >
        <ArrowLeftIcon className="w-5 h-5 mr-2" /> Back
      </button>

      <h2 className="flex items-center justify-between pb-2 mb-6 text-4xl font-medium text-gray-900 border-b-2">
        {data.artistName} - {data.releaseTitle}{" "}
        <Link to={`/pitch/edit/${data.id}`}>
          <PencilIcon className="w-6 h-6" />
        </Link>
      </h2>

      <div className="grid grid-cols-2 gap-6 text-lg text-gray-800">
        <p>
          <span className="font-medium">Release Date:</span> {data.releaseDate}
        </p>
        <p>
          <span className="font-medium">Configuration:</span>{" "}
          {data.configuration}
        </p>
        <p>
          <span className="font-medium">UPC:</span> {data.releaseUPC}
        </p>
        {(data.configuration === "EP" || data.configuration === "Album") &&
          data.focusTrackName && (
            <p>
              <span className="font-medium">Focus Track:</span>{" "}
              {data.focusTrackName}
            </p>
          )}
        <p>
          <span className="font-medium">ISRC:</span> {data.trackISRC}
        </p>
        <p>
          <span className="font-medium">Song Description:</span>{" "}
          {data.songDescription}
        </p>
        <p>
          <span className="font-medium">Main Genre:</span> {data.mainGenre}
        </p>
        <p>
          <span className="font-medium">Song Mood:</span> {data.songMood}
        </p>
        <p>
          <span className="font-medium">Language:</span> {data.language}
        </p>
        <p>
          <span className="font-medium">Featured Artists:</span>{" "}
          {data.featuredArtists}
        </p>
      </div>

      <p className="mt-6 text-lg text-gray-700">
        <span className="font-medium">Description:</span>{" "}
        {data.shortDescription}
      </p>
      <p className="mt-2 text-lg text-gray-700">
        <span className="font-medium">Marketing Drivers:</span>{" "}
        {data.longDescription}
      </p>
      <Disclosure as="div" className="mt-2">
        {({ open }) => (
          <>
            <Disclosure.Button className="flex items-center justify-between w-full px-6 py-3 text-xl font-medium text-left text-gray-900 rounded-md bg-neutral-100 hover:bg-neutral-200 focus:outline-none">
              Lyrics
              <ChevronUpIcon
                className={`${open ? "transform rotate-180" : ""} w-6 h-6 text-gray-600`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="px-6 pt-4 pb-2 rounded-lg shadow-md bg-gray-50">
              <pre className="text-lg text-gray-700 whitespace-pre-wrap">
                {data.lyrics}
              </pre>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      <p className="mt-4">
        <a
          href={data.linkToListen}
          className="text-lg font-semibold text-blue-600 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          🎵 Listen Here
        </a>
      </p>

      {data.promoPhotos && (
        <p className="mt-4">
          <a
            href={data.promoPhotos}
            className="text-lg font-semibold text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            📸 Promo Photos
          </a>
        </p>
      )}

      <h3 className="mt-6 text-2xl font-medium">All DSPs</h3>

      {Object.entries(data).map(
        ([key, value]) =>
          key.endsWith("Info") &&
          value && (
            <Disclosure key={key} as="div" className="mt-4">
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex items-center justify-between w-full px-6 py-3 text-xl font-medium text-left text-gray-900 rounded-md bg-neutral-100 hover:bg-neutral-200 focus:outline-none">
                    {formatKey(key)}
                    
                    <ChevronUpIcon
                      className={`${
                        open ? "transform rotate-180" : ""
                      } w-6 h-6 text-gray-600`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-6 pt-4 pb-2 rounded-lg shadow-md bg-gray-50">
                    <ul className="text-lg text-gray-700 list-disc list-inside">
                      {Object.entries(value as Record<string, any>)
                        .filter(
                          ([subKey, val]) =>
                            !excludedFields.includes(subKey) &&
                            val !== "" &&
                            val !== null &&
                            val !== undefined
                        )
                        .map(([subKey, val]) => (
                          <li className="mt-1" key={subKey}>
                            <span className="mr-2 font-medium uppercase">
                              {formatKey(subKey)}:
                            </span>{" "}
                            {subKey === "createdAt"
                              ? new Date(val).toLocaleDateString()
                              : String(val)}
                          </li>
                        ))}
                    </ul>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          )
      )}
    </div>
  );
};

const formatKey = (key: string) => {
  if (key === "songDetails") return "Release Stage";
  if (key === "SoundCloudInfo") return "Soundcloud Info";
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
};
export default ViewSubmission;
